import {
    ComponentRef,
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import {
    ClickatellFormValidationComponent
} from "../../../components/clickatell-form-validation/clickatell-form-validation.component";
import {FormGroup} from "@angular/forms";

/**
 * FormInput Directive
 *
 * A directive that wraps form input elements with labels and validation components.
 * This directive automatically creates a structured DOM layout for form controls,
 * including labels, optional labels, and validation messages.
 *
 * @description
 * This directive enhances form inputs by:
 * - Creating a wrapper div with 'control-div' class
 * - Adding a label container with the provided label text
 * - Optionally adding an "optional" label indicator
 * - Dynamically creating a ClickatellFormValidationComponent for validation messages
 * - Reorganizing the DOM structure to wrap the input element
 *
 * The resulting DOM structure:
 * ```html
 * <div class="control-div">
 *   <div class="control-label-container">
 *     <label class="control-label">Label Text</label>
 *     <label class="control-optional">Optional Text</label> <!-- if optionalLabel provided -->
 *   </div>
 *   <clickatell-form-validation></clickatell-form-validation> <!-- if activeForm provided -->
 *   <input /> <!-- original input element -->
 * </div>
 * ```
 *
 * @example
 * // Basic usage with required field
 * <input FormInput="Username" name="username" />
 *
 * @example
 * // Usage with optional label
 * <input FormInput="Email" optionalLabel="(Optional)" name="email" />
 *
 * @example
 * // Usage with form validation
 * <input FormInput="Password"
 *        name="password"
 *        [activeForm]="myFormGroup"
 *        type="password" />
 *
 * @example
 * // Usage with textarea
 * <textarea FormInput="Description"
 *           optionalLabel="(Optional)"
 *           name="description"
 *           [activeForm]="myFormGroup"></textarea>
 *
 * @example
 * // Usage with select dropdown
 * <select FormInput="Country"
 *         name="country"
 *         [activeForm]="myFormGroup">
 *   <option value="us">United States</option>
 *   <option value="uk">United Kingdom</option>
 * </select>
 */
@Directive({
    selector: 'input[FormInput], textarea[FormInput], select[FormInput], matSelect[FormInput]',
})
export class InputDirective implements OnInit, OnDestroy {

    /**
     * The main label text to display above the form control.
     * This is the primary input and is required.
     * @required
     */
    @Input('FormInput') label!: string;

    /**
     * Optional supplementary label text (e.g., "(Optional)" or "(Required)").
     * Displayed next to the main label in the label container.
     * @optional
     */
    @Input() optionalLabel?: string;

    /**
     * The FormGroup instance that contains this form control.
     * Required for validation functionality. When provided with a control name,
     * enables automatic validation error display.
     * @optional
     */
    @Input() activeForm?: FormGroup;

    private validationComponentRef?: ComponentRef<ClickatellFormValidationComponent>;


    /**
     * Constructor
     *
     * @param el - ElementRef to access the native DOM element
     * @param renderer - Renderer2 for safe DOM manipulation
     * @param viewContainerRef - ViewContainerRef for dynamic component creation
     */
    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private viewContainerRef: ViewContainerRef
    ) {
    }

    /**
     * Angular lifecycle hook that initializes the directive after data-bound properties are set.
     *
     * @description
     * This method performs the following operations:
     * 1. Extracts the form control name from the input's 'name' attribute
     * 2. Sets an id on the input element if it doesn't already have one
     * 3. Creates a wrapper div structure with 'control-div' class
     * 4. Creates a label container with the primary label
     * 5. Optionally adds a supplementary label if optionalLabel is provided
     * 6. Dynamically creates a ClickatellFormValidationComponent for validation
     * 7. Reorganizes the DOM to wrap the original input element with the new structure
     *
     * @remarks
     * - The directive uses Angular's Renderer2 for safe DOM manipulation
     * - Form control names with special characters (', [, ]) are sanitized
     * - Validation component is only added when both formControlName and activeForm are present
     *
     * @returns void
     */
    ngOnInit() {
        const inputElement = this.el.nativeElement as HTMLInputElement | HTMLTextAreaElement;

        // Extract properties from the input element
        // Remove special characters from the name attribute to get a clean form control name
        const formControlName = inputElement.getAttribute('name')?.replace(/['\[\]]/g, '');

        // Set id attribute if not already present (uses form control name as default)
        if (!inputElement.id) {
            inputElement.id = formControlName || '';
        }

        // Get the parent element to insert the wrapper
        const parent = this.renderer.parentNode(inputElement);

        // Create wrapper div to contain all form control elements
        const wrapper = this.renderer.createElement('div');
        this.renderer.addClass(wrapper, 'control-div');

        // Create label container to hold the main label and optional label
        const labelContainer = this.renderer.createElement('div');
        this.renderer.addClass(labelContainer, 'control-label-container');

        // Create and append the main label element
        const labelElement = this.renderer.createElement('label');
        this.renderer.addClass(labelElement, 'control-label');
        const labelText = this.renderer.createText(this.label);
        this.renderer.appendChild(labelElement, labelText);
        this.renderer.appendChild(labelContainer, labelElement);

        // Add optional label if provided (e.g., "(Optional)" or "(Required)")
        if (this.optionalLabel) {
            const optionalElement = this.renderer.createElement('label');
            this.renderer.addClass(optionalElement, 'control-optional');
            const optionalText = this.renderer.createText(this.optionalLabel);
            this.renderer.appendChild(optionalElement, optionalText);
            this.renderer.appendChild(labelContainer, optionalElement);
        }

        // Create validation component dynamically to display form validation errors
        this.validationComponentRef =  this.viewContainerRef.createComponent(ClickatellFormValidationComponent);

        // Configure validation component if form control name and form group are available
        if (formControlName && this.activeForm) {
            this.validationComponentRef.instance.controlName = formControlName;
            this.validationComponentRef.instance.form = this.activeForm;
        }

        // Insert wrapper before the original input element in the parent
        this.renderer.insertBefore(parent, wrapper, inputElement);

        // Append label container to wrapper
        this.renderer.appendChild(wrapper, labelContainer);

        // Append validation component to wrapper if validation is configured
        if (formControlName && this.activeForm) {
            this.renderer.appendChild(wrapper, this.validationComponentRef.location.nativeElement);
        }

        // Move the original input element into the wrapper
        this.renderer.removeChild(parent, inputElement);
        this.renderer.appendChild(wrapper, inputElement);
    }

    ngOnDestroy() {
        // Prevent memory leaks
        this.validationComponentRef?.destroy();
    }

}
